/* Created by Interactive 63 - https://interactive63.com */
/**
 * Shared helpers for preset definitions: reading current values with fallbacks, common easings,
 * and the slide direction maths.
 */
import { parseColour } from '../colour';
import { GEOMETRY_TAGS, type Easing, type PropertyValue, type Vec2 } from '../model/types';
import type {
  PresetContext,
  PresetDefinition,
  PresetFit,
  PresetKeyframe,
  PresetParamDef,
  PresetParams,
} from './types';

export const REASON_NOT_A_SHAPE = 'Works on shapes, not groups or text';
export const REASON_NO_STROKE = 'Needs a stroke: this shape has no outline';
export const REASON_NO_SOLID_FILL = 'Needs a solid fill colour';
export const REASON_NOT_A_GRADIENT = 'Select a gradient in the Layers panel';

export const LINEAR: Easing = { type: 'linear' };
export const EASE_IN: Easing = { type: 'keyword', name: 'ease-in' };
export const EASE_OUT: Easing = { type: 'keyword', name: 'ease-out' };
export const EASE_IN_OUT: Easing = { type: 'keyword', name: 'ease-in-out' };
export const HOLD: Easing = { type: 'hold' };
export const SLIDE_CURVE: Easing = { type: 'cubic', x1: 0.22, y1: 1, x2: 0.36, y2: 1 };
export const SPRING: Easing = { type: 'spring', stiffness: 170, damping: 26, mass: 1 };
export const BOUNCE: Easing = { type: 'bounce', bounces: 3, decay: 0.5 };
/** Overshoots the end value slightly before settling on it. */
export const BACK_OUT: Easing = { type: 'cubic', x1: 0.34, y1: 1.56, x2: 0.64, y2: 1 };
/** Pulls back slightly before accelerating away. */
export const BACK_IN: Easing = { type: 'cubic', x1: 0.36, y1: 0, x2: 0.66, y2: -0.56 };

export const DIRECTION_PARAM: PresetParamDef = {
  key: 'direction',
  label: 'Direction',
  type: 'select',
  default: 'left',
  options: [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'up', label: 'Up' },
    { value: 'down', label: 'Down' },
  ],
};

export const DISTANCE_PARAM: PresetParamDef = {
  key: 'distance',
  label: 'Distance',
  type: 'number',
  default: 40,
  min: 0,
  max: 500,
  step: 5,
  unit: '% of size',
};

/** Transform origins for presets that swing from the top or pivot on the base, in percent. */
export const TOP_CENTRE: Vec2 = { x: 50, y: 0 };
export const BOTTOM_CENTRE: Vec2 = { x: 50, y: 100 };

export const AXIS_PARAM: PresetParamDef = {
  key: 'axis',
  label: 'Axis',
  type: 'select',
  default: 'x',
  options: [
    { value: 'x', label: 'X (flips left to right)' },
    { value: 'y', label: 'Y (flips top to bottom)' },
  ],
};

/** A scale with one axis multiplied by a factor. */
export function scaleAlongAxis(scale: Vec2, axis: string, factor: number): Vec2 {
  return axis === 'y' ? { x: scale.x, y: scale.y * factor } : { x: scale.x * factor, y: scale.y };
}

/** A keyframe at an offset. */
export function kf(offset: number, value: PropertyValue, easing: Easing = LINEAR): PresetKeyframe {
  return { offset, value, easing };
}

/** A number from a property value, or a fallback. */
export function numberOf(value: PropertyValue | undefined, fallback: number): number {
  return typeof value === 'number' ? value : fallback;
}

/** A vector from a property value, or a fallback. */
export function vecOf(value: PropertyValue | undefined, fallback: Vec2): Vec2 {
  return typeof value === 'object' && 'x' in value && 'y' in value
    ? { x: value.x, y: value.y }
    : fallback;
}

/** A text value from a property value, or a fallback. */
export function textOf(value: PropertyValue | undefined, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

/** A numeric parameter with a fallback. */
export function paramNumber(params: PresetParams, key: string, fallback: number): number {
  const value = params[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** A text parameter with a fallback. */
export function paramText(params: PresetParams, key: string, fallback: string): string {
  const value = params[key];
  return typeof value === 'string' ? value : fallback;
}

export const opacityOf = (ctx: PresetContext): number => numberOf(ctx.current.opacity, 1);
export const translateOf = (ctx: PresetContext): Vec2 =>
  vecOf(ctx.current.translate, { x: 0, y: 0 });
export const scaleOf = (ctx: PresetContext): Vec2 => vecOf(ctx.current.scale, { x: 1, y: 1 });
export const rotateOf = (ctx: PresetContext): number => numberOf(ctx.current.rotate, 0);
export const skewOf = (ctx: PresetContext): Vec2 => vecOf(ctx.current.skew, { x: 0, y: 0 });
export const strokeWidthOf = (ctx: PresetContext): number => numberOf(ctx.current.strokeWidth, 1);
export const fillOpacityOf = (ctx: PresetContext): number => numberOf(ctx.current.fillOpacity, 1);

/** Multiplies a scale vector by a factor. */
export function scaleBy(scale: Vec2, factor: number): Vec2 {
  return { x: scale.x * factor, y: scale.y * factor };
}

/** Whether a preset that draws a stroke can apply: a stroked basic shape or path. */
export function strokedGeometryFit(ctx: PresetContext): PresetFit {
  if (!GEOMETRY_TAGS.includes(ctx.tag)) return REASON_NOT_A_SHAPE;
  return ctx.hasStroke ? true : REASON_NO_STROKE;
}

/** Whether an element has a plain fill colour that a preset can animate. */
export function solidFillFit(ctx: PresetContext): PresetFit {
  const fill = textOf(ctx.current.fill, 'none');
  return fill !== 'none' && parseColour(fill) !== null ? true : REASON_NO_SOLID_FILL;
}

/** Whether a stroked shape also has a plain fill colour, for presets that use both. */
export function strokedAndFilledFit(ctx: PresetContext): PresetFit {
  const stroke = strokedGeometryFit(ctx);
  return stroke === true ? solidFillFit(ctx) : stroke;
}

/** The reason a preset cannot apply to an element, or null when it can. */
export function presetReason(preset: PresetDefinition, ctx: PresetContext): string | null {
  const fit = preset.appliesTo(ctx);
  return fit === true ? null : fit;
}

/** The translation that moves an element away in a direction by a percentage of its size. */
export function slideOffset(params: PresetParams, ctx: PresetContext): Vec2 {
  const percent = paramNumber(params, 'distance', 40) / 100;
  switch (paramText(params, 'direction', 'left')) {
    case 'right':
      return { x: ctx.bbox.width * percent, y: 0 };
    case 'up':
      return { x: 0, y: -ctx.bbox.height * percent };
    case 'down':
      return { x: 0, y: ctx.bbox.height * percent };
    default:
      return { x: -ctx.bbox.width * percent, y: 0 };
  }
}

/** Adds a vector to a position. */
export function shifted(position: Vec2, by: Vec2): Vec2 {
  return { x: position.x + by.x, y: position.y + by.y };
}
